"use client";
import { Track } from "@/src/lib/tracking";

import { useEffect, useRef } from "react";
import { useParams, usePathname } from "next/navigation";
import { deviceDetector } from "@ospweb/sdk-ui";

/** 宽度阈值：低于为移动端(H5)，高于为桌面端(PC)，与 useIsPc 等保持一致 */
const WIDTH_THRESHOLD = 964;
/** resize 防抖时间，避免频繁检测与刷新 */
const RESIZE_DEBOUNCE_MS = 400;

/**
 * 由于iphone safari 浏览器会隐藏UA标识导致服务端渲染识别为PC，所以要在客户端渲染的时候做设备检测，如果识别错误，需要设置cookie并重定向到移动端路由
 *
 * 功能说明：
 * 1. 严格检查执行条件：必须是 Safari 浏览器（非 app）、device 是 desktop、设备宽度是移动端
 * 2. 如果满足条件，设置 x-force-h5=1 cookie 并刷新页面，服务端会根据 cookie 路由到 h5
 * 3. 支持窗口 resize：缩小到阈值以下自动切 H5，放大到阈值以上自动切 PC（防抖处理，兼顾性能）
 */

/**
 * 检查是否为真正的 app 环境（不依赖 cookie）
 * 通过 WebView 标志检测，而不是 cookie
 * @returns 如果是真正的 app 环境返回 true
 */
const isRealApp = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  // 检测 iOS WebView（通过 messageHandlers）
  if ((window as any).webkit?.messageHandlers?.bridge) {
    return true;
  }

  // 检测 Android WebView（通过 JavaScript Interface）
  if (
    (window as any).bridge &&
    typeof (window as any).bridge.handleMessage === "function"
  ) {
    return true;
  }

  return false;
};

/**
 * 检查是否满足重定向条件
 * @returns 是否满足所有条件
 */
const checkRedirectConditions = (
  device: string | undefined,
): { shouldRedirect: boolean; reason?: string } => {
  // 条件 0: 必须不是 app 环境（必须是浏览器）
  // 注意：iOS App WebView 的 UA 可能包含 "safari"，所以需要先排除 app 环境
  if (isRealApp()) {
    return { shouldRedirect: false, reason: "当前是 app 环境，不需要处理" };
  }

  // 条件 1: 必须是 Safari 浏览器
  const isSafari = deviceDetector.checkIfSafari();
  if (!isSafari) {
    return { shouldRedirect: false, reason: "不是 Safari 浏览器" };
  }

  // 条件 2: device 必须是 desktop
  if (device !== "desktop") {
    return {
      shouldRedirect: false,
      reason: `device 不是 desktop，当前为: ${device}`,
    };
  }

  // 条件 3: 设备宽度必须是移动端宽度（< WIDTH_THRESHOLD）
  const windowWidth = window.innerWidth;
  const isMobileWidth = windowWidth < WIDTH_THRESHOLD;
  if (!isMobileWidth) {
    return {
      shouldRedirect: false,
      reason: `设备宽度不是移动端宽度，当前为: ${windowWidth}px`,
    };
  }

  return { shouldRedirect: true };
};

/**
 * 粗略判断当前是否为“明显的 PC 环境”
 * 仅用于决定是否可以安全清除 x-force-h5
 * ipad
 */
const isObviouslyDesktopEnvironment = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const windowWidth = window.innerWidth;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const isPcScreen =
    screenWidth > screenHeight && screenWidth > WIDTH_THRESHOLD;

  // 窗口宽度足够大且屏幕为横屏大屏，认为是 PC
  return windowWidth >= WIDTH_THRESHOLD && isPcScreen;
};

/**
 * 检查是否已经有 x-force-h5 cookie
 */
const hasForceH5Cookie = (): boolean => {
  if (typeof document === "undefined") {
    return false;
  }
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "x-force-h5" && value === "1") {
      return true;
    }
  }
  return false;
};

/**
 * 设置 x-force-h5 cookie 并刷新页面
 */
const setForceH5CookieAndReload = (
  device: string | undefined,
  locale: string | undefined,
) => {
  // 设置 cookie（有效期 1 年）
  const maxAge = 60 * 60 * 24 * 365; // 1年
  document.cookie = `x-force-h5=1; max-age=${maxAge}; path=/; SameSite=Lax`;

  Track.log(
    "info",
    "useRedirectToH5",
    "检测到设备识别错误，设置 x-force-h5 cookie",
    {
      windowWidth: window.innerWidth,
      device,
      locale,
    },
  );

  // 刷新页面，让服务端读取 cookie 并路由到 h5
  window.location.reload();
};

/**
 * 清除 x-force-h5 cookie 并刷新/跳转到 PC
 * 场景：PC 曾因窗口缩小被误导到 H5，恢复为 PC 窗口后需要回到 PC 版
 * @param pathname 当前 pathname；当 device 为 h5 时用于跳转到 desktop 路由
 */
const clearForceH5CookieAndReload = (
  device: string | undefined,
  locale: string | undefined,
  pathname?: string,
) => {
  document.cookie = "x-force-h5=; max-age=0; path=/; SameSite=Lax";

  Track.log(
    "info",
    "useRedirectToH5",
    "检测到当前为 PC 环境，清除 x-force-h5 cookie",
    {
      windowWidth: window.innerWidth,
      device,
      locale,
    },
  );

  if (device === "h5" && pathname && pathname.includes("/h5/")) {
    window.location.pathname = pathname.replace(/\/h5\//, "/desktop/");
  } else {
    window.location.reload();
  }
};

export const useRedirectToH5 = () => {
  const params = useParams();
  const pathname = usePathname();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // 只在客户端执行
    if (typeof window === "undefined") {
      return;
    }

    try {
      // 步骤 1: 如果已经有 x-force-h5 cookie，判断是否需要清除
      const device = params?.device as string | undefined;
      const locale = params?.locale as string | undefined;

      // 步骤 1: 如果已经有 x-force-h5 cookie，判断是否需要清除
      if (hasForceH5Cookie()) {
        // 如果当前已经是明显的 PC 环境，则认为之前是 PC 缩小窗口误触发，清除 cookie
        if (isObviouslyDesktopEnvironment()) {
          clearForceH5CookieAndReload(device, locale, pathname);
          return;
        }

        // 移动端等场景，保留 cookie，避免来回跳转
        return;
      }

      // 步骤 2: 避免重复处理
      if (hasProcessed.current) {
        return;
      }

      // 步骤 3: 检查执行条件（包含 app 环境检查）
      const { shouldRedirect } = checkRedirectConditions(device);

      if (!shouldRedirect) {
        return;
      }

      // 步骤 4: 所有条件都满足，设置 cookie 并刷新页面
      hasProcessed.current = true;
      setForceH5CookieAndReload(device, locale);
      setForceH5CookieAndReload(device, locale);
    } catch (error) {
      Track.error(error, { tag: "useRedirectToH5", msg: "处理失败" });
    }
  }, [pathname, params]);

  // 窗口 resize 检测：缩小切 H5、放大切 PC，防抖以兼顾性能
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const device = params?.device as string | undefined;
    const locale = params?.locale as string | undefined;

    // desktop 或 h5 路由且 Safari、非 app 时监听 resize（h5 时需在放大后切回 PC）
    const isSafari = deviceDetector.checkIfSafari();
    if ((device !== "desktop" && device !== "h5") || isRealApp() || !isSafari) {
      return;
    }

    const handleResize = () => {
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      resizeTimerRef.current = setTimeout(() => {
        resizeTimerRef.current = null;
        const w = window.innerWidth;
        const hasCookie = hasForceH5Cookie();

        // 窗口放大到阈值以上：沿用 isObviouslyDesktopEnvironment() 判断，不单按窗口宽度
        if (
          hasCookie &&
          w >= WIDTH_THRESHOLD &&
          isObviouslyDesktopEnvironment()
        ) {
          clearForceH5CookieAndReload(device, locale, pathname);
          return;
        }
        if (!hasCookie && w < WIDTH_THRESHOLD && device === "desktop") {
          const { shouldRedirect } = checkRedirectConditions(device);
          if (shouldRedirect) {
            setForceH5CookieAndReload(device, locale);
          }
        }
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
  }, [pathname, params]);
};
